import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@neo/lib/utils'
import { AnimatePresence, m } from 'framer-motion'
import { CornerDownRight, GripVertical, Pencil, Send, X } from 'lucide-react'
import { useState } from 'react'
import { DURATION, EASE_OUT } from '../motion'
import { drainQueueHead, type QueuedMessage, selectIsRunActive, useVibeAgentStore } from '../store'
import { useVibeAgentApi } from './use-vibe-agent-api'

interface QueueRowProps {
  item: QueuedMessage
  isHead: boolean
  canSendNext: boolean
  isEditing: boolean
  dimmed: boolean
  draft: string
  setDraft: (value: string) => void
  onStartEdit: () => void
  onCommitEdit: () => void
  onCancelEdit: () => void
  onSend: () => void
  onRemove: () => void
}

// Codex-style row: drag grip fades in on hover, the row dims while dragged.
// Only the grip carries the sortable listeners so text/buttons stay clickable.
// dnd-kit's transform lives on an OUTER plain div — the inner m.div owns the
// framer enter/exit transforms, so the two never fight over one style.
const QueueRow = ({
  item,
  isHead,
  canSendNext,
  isEditing,
  dimmed,
  draft,
  setDraft,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onSend,
  onRemove,
}: QueueRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
      }}
      // Dim on THIS wrapper, not the m.div: framer owns the inner inline
      // opacity (enter/exit fades) and silently wins any class or animate
      // update against it.
      className={cn('transition-opacity duration-150', (dimmed || isDragging) && 'opacity-60')}
    >
      <m.div
        initial={{ opacity: 0, transform: 'translateY(4px)' }}
        animate={{ opacity: 1, transform: 'translateY(0px)' }}
        exit={{
          opacity: 0,
          transform: 'translateY(4px)',
          transition: { duration: DURATION.exitFast, ease: EASE_OUT },
        }}
        transition={{ duration: DURATION.enter, ease: EASE_OUT }}
        className={cn(
          'group mx-1.5 flex items-center gap-1 rounded-lg px-1.5 py-1.5 transition-colors duration-150 hover:bg-background/80',
          isDragging && 'bg-background/80',
        )}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          className={cn(
            'shrink-0 -ml-0.5 p-0.5 rounded touch-none text-secondary/70',
            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-150',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40',
            isDragging ? 'cursor-grabbing opacity-100' : 'cursor-grab',
          )}
          aria-label="Drag to reorder"
          data-testid="button-drag-queued"
        >
          <GripVertical className="w-3 h-3" />
        </button>
        <CornerDownRight className="w-3.5 h-3.5 shrink-0 text-secondary/70" />
        {isEditing ? (
          <textarea
            value={draft}
            aria-label="Edit queued message"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onCommitEdit()
              }
              if (e.key === 'Escape') onCancelEdit()
            }}
            onBlur={onCommitEdit}
            rows={2}
            className="flex-1 resize-none bg-transparent text-p-400 text-primary outline-none"
            data-testid="input-edit-queued"
            // biome-ignore lint/a11y/noAutofocus: focus follows the explicit edit action
            autoFocus
          />
        ) : (
          <>
            <span className="flex-1 text-p-400 text-primary truncate">{item.text}</span>
            <span className="flex items-center gap-0.5">
              {isHead && canSendNext && (
                <button
                  type="button"
                  onClick={onSend}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-st1 font-jetbrains text-label uppercase text-secondary hover:text-foreground transition-colors duration-150"
                  aria-label="Send now"
                  data-testid="button-send-next"
                >
                  <Send className="w-3 h-3" /> Send
                </button>
              )}
              <button
                type="button"
                onClick={onStartEdit}
                className="p-1 rounded hover:bg-st1 text-secondary/70 hover:text-secondary"
                aria-label="Edit queued message"
                data-testid="button-edit-queued"
              >
                <Pencil className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="p-1 rounded hover:bg-st1 text-secondary/70 hover:text-secondary"
                aria-label="Remove queued message"
                data-testid="button-remove-queued"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          </>
        )}
      </m.div>
    </div>
  )
}

/**
 * Codex-style queue panel: its own rounded bordered container sitting flush
 * above the composer — rows only, no header. Each row: drag grip (reorder),
 * corner glyph, one-line text, always-visible actions (Send on the head when
 * dispatch is legal, edit, remove). Items are FE-local until dispatched;
 * auto-drain lives in use-vibe-agent.ts.
 */
export const VibeQueueStack = () => {
  const queuedMessages = useVibeAgentStore((state) => state.queuedMessages)
  const gate = useVibeAgentStore((state) => state.gate)
  const isRunActive = useVibeAgentStore(selectIsRunActive)
  const removeQueued = useVibeAgentStore((state) => state.removeQueued)
  const editQueued = useVibeAgentStore((state) => state.editQueued)
  const reorderQueue = useVibeAgentStore((state) => state.reorderQueue)
  const queuePaused = useVibeAgentStore((state) => state.queuePaused)
  const resumeQueue = useVibeAgentStore((state) => state.resumeQueue)
  const { send } = useVibeAgentApi()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  // Activation distance keeps plain grip clicks (and future grip focus) from
  // starting a drag; keyboard sensor gives Space/arrows reordering for free.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // Manual dispatch is hidden while busy (drain handles it) AND while a gate
  // is open — a send during a gate would silently become the gate's answer.
  const canSendNext = !isRunActive && gate === null

  const startEdit = (id: string, text: string) => {
    setEditingId(id)
    setDraft(text)
  }

  const commitEdit = () => {
    if (editingId) editQueued(editingId, draft)
    setEditingId(null)
  }

  // Resume = unpause; if it's already the user's turn, dispatch the head now
  // (no completed transition is coming to do it).
  const handleResume = () => {
    resumeQueue()
    const head = queuedMessages[0]
    if (head && canSendNext) drainQueueHead(send, head.id)
  }

  // No local list copy: the store stays the single source of truth (a drain or
  // remove can land mid-drag), and reorderQueue rejects stale permutations.
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const ids = queuedMessages.map((q) => q.id)
    const from = ids.indexOf(String(active.id))
    const to = ids.indexOf(String(over.id))
    if (from < 0 || to < 0) return
    reorderQueue(arrayMove(ids, from, to))
  }

  return (
    // Same visual mechanism as the "Generating..." strip: a bg-bg2 card that
    // peeks out from behind the composer box — its bottom padding is tucked
    // under the box (negative margin), so it reads as sitting ON the input.
    // The empty-check lives INSIDE AnimatePresence so removing the last item
    // plays the panel's exit instead of unmounting it mid-idiom.
    <AnimatePresence initial={false}>
      {queuedMessages.length > 0 && (
        <m.div
          initial={{ opacity: 0, transform: 'translateY(10px)' }}
          animate={{ opacity: 1, transform: 'translateY(0px)' }}
          exit={{
            opacity: 0,
            transform: 'translateY(10px)',
            transition: { duration: DURATION.exit, ease: EASE_OUT },
          }}
          transition={{ duration: DURATION.panel, ease: EASE_OUT }}
          className="relative z-0 mx-[2px] rounded-t-2xl bg-bg2 pt-1.5 pb-6 -mb-4"
          data-testid="vibe-queue-stack"
        >
          {queuePaused && (
            <div
              className="mx-3 mb-1 flex items-center justify-between gap-2"
              data-testid="banner-queue-paused"
            >
              <span className="text-xs-500 text-secondary">Queue paused after an error.</span>
              <button
                type="button"
                onClick={handleResume}
                className="shrink-0 px-1.5 py-0.5 rounded font-jetbrains text-label uppercase text-foreground hover:bg-st1 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
                data-testid="button-resume-queue"
              >
                Resume
              </button>
            </div>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={queuedMessages.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              <AnimatePresence initial={false}>
                {queuedMessages.map((item, idx) => (
                  <QueueRow
                    key={item.id}
                    item={item}
                    isHead={idx === 0}
                    canSendNext={canSendNext}
                    isEditing={editingId === item.id}
                    dimmed={queuePaused && editingId !== item.id}
                    draft={draft}
                    setDraft={setDraft}
                    onStartEdit={() => startEdit(item.id, item.text)}
                    onCommitEdit={commitEdit}
                    onCancelEdit={() => setEditingId(null)}
                    onSend={() => drainQueueHead(send, item.id)}
                    onRemove={() => removeQueued(item.id)}
                  />
                ))}
              </AnimatePresence>
            </SortableContext>
          </DndContext>
        </m.div>
      )}
    </AnimatePresence>
  )
}
